import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Flags from 'country-flag-icons/react/3x2'
import { scaleLinear } from 'd3-scale'
import _ from 'lodash'
import { Box } from '@mui/material'
import {
  calculateWeightedGlobalBarChart,
  generateGlobalBarChartData,
  getCountryLongCodeFromCountryCode,
  isEmptyData,
} from 'utils'
import CountryTick from 'components/common/CountryTick/CountryTick'
import BarChartComponent from 'components/common/BarChartCountry/BarChartComponent'
import { BarChartGlobalData, DownloadType } from 'types'
import { useActions, useAppSelector } from 'hooks'
import Tooltip from './components/CustomToolTip'
import DownloadImages from 'components/common/Download/DownloadImages'
import { useImageDownload } from 'components/common/Download/useImageDownload'
import { DOWNLOAD_FILE_NAME, G20_COUNTRIES, METRIC_ENDPOINT_RANK, METRIC_ENDPOINT_WEIGHT } from 'constant'
import ChartHeader from 'components/common/ChartHeader/ChartHeader'
import ChartFooter from 'components/common/ChartFooter/ChartFooter'

const renderTick = (dataParam: BarChartGlobalData) =>
  scaleLinear()
    .domain([0, _.max(dataParam.map((el) => el.score)) ?? 0])
    .ticks()
    .filter((el) => el % 10 === 0)

export default function Chart() {
  const configuration = useAppSelector(({ vibrancy }) => vibrancy.configuration)
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const g20Countries = useAppSelector(({ vibrancy }) => vibrancy.g20Countries)
  const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)
  const selectedMetricValue = useAppSelector(({ vibrancy }) => vibrancy.configuration.metricValue)
  const currentYear = useAppSelector(({ vibrancy }) => vibrancy.configuration.yearFilter.currentYear)
  const { setBarChartGlobalData, setRanking, setCountry, setCountryType, setShowDwnLoader, setComparingCountries } =
    useActions()
  const [filteredCountryData, setFilteredCountryData] = useState(countryData)

  useEffect(() => {
    if (g20Countries) {
      let G20Codes = G20_COUNTRIES.map((i) => i.countryCode)
      setFilteredCountryData(() => countryData.filter((i) => G20Codes.includes(i.countryCode)))
    } else {
      setFilteredCountryData(countryData)
    }
  }, [g20Countries, countryData]);

  const renderChartData = useMemo(() => {
    const dataReturn = calculateWeightedGlobalBarChart(
      metricGroups,
      selectedMetricValue,
      filteredCountryData,
      currentYear,
    )
    return dataReturn
  }, [metricGroups, selectedMetricValue, filteredCountryData, currentYear])

  const data = useMemo<BarChartGlobalData>(() => {
    const { yearFilter, metricValue } = configuration
    if (metricGroups.length === 0 || filteredCountryData?.length === 0 || yearFilter?.yearRange.length === 0) return []

    const dataReturn = generateGlobalBarChartData(metricGroups, renderChartData, {
      globalYear: currentYear,
      metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
      metricEndpointRank: METRIC_ENDPOINT_RANK,
      metricValue: metricValue,
    })
    return dataReturn
  }, [filteredCountryData, metricGroups, renderChartTimes, renderChartData, currentYear])

  const renderCustomAxisTick = useCallback(
    ({ payload, y }: { payload: { value: string }; y: number }) => {
      const countryItem = data.find((el) => el.name === payload.value)
      return (
        <CountryTick
          countryCode={payload.value as keyof typeof Flags}
          id={countryItem?.id ?? 0}
          countryName={countryItem?.countryName ?? ''}
          position={{ y }}
          onClick={() => {
            setRanking({ ranking: 'national' })
            setCountryType({ countryType: 'national' })
            setCountry({ country: getCountryLongCodeFromCountryCode(filteredCountryData, payload.value) as string })
          }}
        />
      )
    },
    [filteredCountryData, data] // dependencies
  )

  // console.log('___render_BarChart:', data)
  // console.log('___data_metricGroups:', metricGroups)

  useEffect(() => {
    setBarChartGlobalData(data)
  }, [data])

  // Download images
  const fileNameDownload = DOWNLOAD_FILE_NAME.globalAiVibrancyRankingBar
  const [clickDownload, setClickDownload] = useState<DownloadType>(null)
  const { handleCallDownload, divRef } = useImageDownload()
  const handleDivDownload = (type: DownloadType) => {
    setShowDwnLoader(true)
    setClickDownload(type)
  }
  useEffect(() => {
    if (clickDownload) {
      handleCallDownload(clickDownload, { fileName: fileNameDownload, orientation: 'portrait', isSquareDownload: true })
      setClickDownload(null)
    }
  }, [clickDownload])

  const yearSource = new Date().getFullYear()

  useEffect(() => {
    if (data) {
      const top4Countries = data.slice(0, 4).map((eachData) => {
        return getCountryLongCodeFromCountryCode(filteredCountryData, eachData.name) as string
      })
      setComparingCountries({ countries: top4Countries })
    }
  }, [data])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      <DownloadImages
        handleDownPNG={() => handleDivDownload('png')}
        handleDownJPG={() => handleDivDownload('jpg')}
        handleDownPDF={() => handleDivDownload('pdf')}
      />
      <Box sx={{ padding: '24px 24px 0px 24px' }}>
        <ChartHeader
          header1={`${configuration.yearFilter.currentYear} Global AI Vibrancy Ranking ${selectedMetricValue === 'perCapita' ? '(Per Capita)' : '(Absolute)'}`}
          header2={`Weighted Index Score | Source: ${yearSource} AI Index`}
        />
        <br />
        {!isEmptyData(data) && (
          <BarChartComponent
            data={data}
            isStackedBarChart
            xAxisTicks={renderTick(data)}
            yAxisTickRenderFunc={renderCustomAxisTick}
            tooltipContent={`Click on an individual country to visit that country's profile`}
            CustomToolTipComponent={Tooltip}
            onClickBarChartEvent={(eleOnClick) => {
              setRanking({ ranking: 'national' })
              setCountryType({ countryType: 'national' })
              setCountry({ country: getCountryLongCodeFromCountryCode(filteredCountryData, eleOnClick.name) as string })
            }}
            configuration={{ currentGrouping: configuration.grouping.currentGrouping }}
          />
        )}
        {g20Countries && (
          <ChartFooter
            description={
              'The Group of 20 (G20) countries represented in this ranking excludes Argentina, Indonesia, the European Union, and the African Union, which are typically part of the G20 organizational structure but are not included in this specific dataset.'
            }
          />
        )}
      </Box>
      <Box sx={{ zIndex: -1, position: 'absolute', padding: '24px 24px 0px 24px' }} ref={divRef}>
        <ChartHeader
          header1={`${configuration.yearFilter.currentYear} Global AI Vibrancy Ranking ${selectedMetricValue === 'perCapita' ? '(Per Capita)' : '(Absolute)'}`}
          header2={`Weighted Index Score | Source: ${yearSource} AI Index`}
        />
        <br />
        {!isEmptyData(data) && (
          <BarChartComponent
            data={data}
            isStackedBarChart
            xAxisTicks={renderTick(data)}
            yAxisTickRenderFunc={renderCustomAxisTick}
            tooltipContent={`Click on an individual country to visit that country's profile`}
            CustomToolTipComponent={Tooltip}
            onClickBarChartEvent={(eleOnClick) => {
              setRanking({ ranking: 'national' })
              setCountryType({ countryType: 'national' })
              setCountry({ country: getCountryLongCodeFromCountryCode(filteredCountryData, eleOnClick.name) as string })
            }}
            configuration={{ currentGrouping: configuration.grouping.currentGrouping }}
            is4Downloaded
          />
        )}
        {g20Countries && (
          <ChartFooter
            description={
              'The Group of 20 (G20) countries represented in this ranking excludes Argentina, Indonesia, the European Union, and the African Union, which are typically part of the G20 organizational structure but are not included in this specific dataset.'
            }
          />
        )}
      </Box>
    </Box>
  )
}
